import { Collection } from '@/kernel/collection';

import StringCell from '@/components/cells/string-cell.vue';
import DateCell from '@/components/cells/date-cell.vue';
import BooleanCell from '@/components/cells/boolean-cell.vue';
import EditCollectionCell from '@/components/cells/edit-collection-cell.vue';
import CollectionNameCell from '@/components/cells/collection-name-cell.vue';

import { DateRangeFilterAdapter, SelectFilterAdapter, SingleRelationFilterAdapter, SingleRelationInputAdapter } from '@/kernel/adapters';

import DateRangeInput from '@/components/inputs/date-range-input.vue';
import TextInput from '@/components/inputs/text-input.vue';
import TextareaInput from '@/components/inputs/textarea-input.vue';
import CheckboxInput from '@/components/inputs/checkbox-input.vue';
import SelectInput from '@/components/inputs/select-input.vue';
import SingleRelationInput from '@/components/inputs/single-relation-input.vue';
import NumberInput from '@/components/inputs/number-input.vue';

import { scoreMap } from '@/kernel/scoring';

const isPublishedOptions = [
  {value: null, label: 'Any'},
  {value: false, label: 'Unpublished'},
  {value: true, label: 'Published'}
];

const isDeprecatedOptions = [
  {value: null, label: 'Any'},
  {value: false, label: 'Current'},
  {value: true, label: 'Deprecated'}
];

export class Objective extends Collection {
  config = {
    autoRoute: true,
    requiresSuperAdmin: true,
    key: 'objective',
    pluralLabel: 'Objectives',
    singleLabel: 'Objective',
    indexUrl: '/objective',
    createUrl: '/objective/create',
    searchKeys: ['name', 'code'],
    editUrlPrefix: '/objective',
    idKey: 'id',
    nameKey: 'name',
    payloadEndpoint: '/objective',
    defaultSort: {
      key: 'name',
      order: 1
    },
    indexColumns: [
      {name: 'Actions', key: 'edit', sortable: false, cell: EditCollectionCell, props: {collection: 'objective'}},
      {name: 'Name', key: 'name', sortable: true, cell: StringCell},
      {name: 'Version', key: 'version', sortable: true, cell: StringCell},
      {name: 'Theme', key: 'theme', sortable: true, cell: CollectionNameCell, props: {collection: 'theme'}},
      {name: 'Is Published', key: 'isPublished', sortable: false, cell: BooleanCell},
      {name: 'Is Deprecated', key: 'isDeprecated', sortable: false, cell: BooleanCell},
      {name: 'Updated', key: 'updatedAt', sortable: true, cell: DateCell},
      {name: 'Created', key: 'createdAt', sortable: true, cell: DateCell}
    ],
    indexFilters: [
      {key: 'theme', label: 'Theme', input: SingleRelationInput, adapter: SingleRelationFilterAdapter('theme'), props: {label: 'Theme', collection: 'theme'}},
      {key: 'isPublished', label: 'Is Published', input: SelectInput, adapter: SelectFilterAdapter, props: {label: 'Is Published', options: isPublishedOptions}},
      {key: 'isDeprecated', label: 'Is Deprecated', input: SelectInput, adapter: SelectFilterAdapter, props: {label: 'Is Deprecated', options: isDeprecatedOptions}},
      {key: 'updatedAt', label: 'Updated at', input: DateRangeInput, adapter: DateRangeFilterAdapter, props: {label: 'Updated at'}},
      {key: 'createdAt', label: 'Created at', input: DateRangeInput, adapter: DateRangeFilterAdapter, props: {label: 'Created at'}}
    ],
    createFormFields: [
      {key: 'isPublished', label: 'Is Published', input: CheckboxInput, props: {label: 'Is Published'}},
      {key: 'isDeprecated', label: 'Is Deprecated', input: CheckboxInput, props: {label: 'Is Deprecated'}},
      {key: 'theme', label: 'Theme', input: SingleRelationInput, adapter: SingleRelationInputAdapter('theme'), props: {label: 'Theme', collection: 'theme'}},
      {key: 'name', label: 'Name', input: TextInput, props: {label: 'Name'}},
      {key: 'code', label: 'Code', input: TextInput, props: {label: 'Code'}},
      {key: 'version', label: 'Version', input: NumberInput, props: {label: 'Version'}},
      {key: 'objective', label: 'Objective', input: TextareaInput, props: {label: 'Objective'}},
      {key: 'question', label: 'Question', input: TextareaInput, props: {label: 'Question'}},
      {key: 'guidanceNotes', label: 'Guidance Notes', input: TextareaInput, props: {label: 'Guidance Notes'}},
      {key: 'scoreGuideA', label: `Score Guide – ${scoreMap.getLabel(0)}`, input: TextareaInput, props: {label: `Score Guide – ${scoreMap.getLabel(0)}`}},
      {key: 'scoreGuideB', label: `Score Guide – ${scoreMap.getLabel(-1)}`, input: TextareaInput, props: {label: `Score Guide – ${scoreMap.getLabel(-1)}`}},
      {key: 'scoreGuideC', label: `Score Guide – ${scoreMap.getLabel(-2)}`, input: TextareaInput, props: {label: `Score Guide – ${scoreMap.getLabel(-2)}`}},
      {key: 'scoreGuideD', label: `Score Guide – ${scoreMap.getLabel(-3)}`, input: TextareaInput, props: {label: `Score Guide – ${scoreMap.getLabel(-3)}`}},
      {key: 'upliftGuideDtoC', label: `Uplift Guide – ${scoreMap.getLabel(-3)} > ${scoreMap.getLabel(-2)}`, input: TextareaInput, props: {label: `Uplift Guide – ${scoreMap.getLabel(-3)} > ${scoreMap.getLabel(-2)}`}},
      {key: 'upliftGuideCtoB', label: `Uplift Guide – ${scoreMap.getLabel(-2)} > ${scoreMap.getLabel(-1)}`, input: TextareaInput, props: {label: `Uplift Guide – ${scoreMap.getLabel(-2)} > ${scoreMap.getLabel(-1)}`}},
      {key: 'upliftGuideBtoA', label: `Uplift Guide – ${scoreMap.getLabel(-1)} > ${scoreMap.getLabel(0)}`, input: TextareaInput, props: {label: `Uplift Guide – ${scoreMap.getLabel(-1)} > ${scoreMap.getLabel(0)}`}}
    ],
    editFormFields: [
      {key: 'isPublished', label: 'Is Published', input: CheckboxInput, props: {label: 'Is Published'}},
      {key: 'isDeprecated', label: 'Is Deprecated', input: CheckboxInput, props: {label: 'Is Deprecated'}},
      {key: 'theme', label: 'Theme', input: SingleRelationInput, adapter: SingleRelationInputAdapter('theme'), props: {label: 'Theme', collection: 'theme'}},
      {key: 'name', label: 'Name', input: TextInput, props: {label: 'Name'}},
      {key: 'code', label: 'Code', input: TextInput, props: {label: 'Code'}},
      {key: 'version', label: 'Version', input: NumberInput, props: {label: 'Version'}},
      {key: 'objective', label: 'Objective', input: TextareaInput, props: {label: 'Objective'}},
      {key: 'question', label: 'Question', input: TextareaInput, props: {label: 'Question'}},
      {key: 'guidanceNotes', label: 'Guidance Notes', input: TextareaInput, props: {label: 'Guidance Notes'}},
      {key: 'scoreGuideA', label: `Score Guide – ${scoreMap.getLabel(0)}`, input: TextareaInput, props: {label: `Score Guide – ${scoreMap.getLabel(0)}`}},
      {key: 'scoreGuideB', label: `Score Guide – ${scoreMap.getLabel(-1)}`, input: TextareaInput, props: {label: `Score Guide – ${scoreMap.getLabel(-1)}`}},
      {key: 'scoreGuideC', label: `Score Guide – ${scoreMap.getLabel(-2)}`, input: TextareaInput, props: {label: `Score Guide – ${scoreMap.getLabel(-2)}`}},
      {key: 'scoreGuideD', label: `Score Guide – ${scoreMap.getLabel(-3)}`, input: TextareaInput, props: {label: `Score Guide – ${scoreMap.getLabel(-3)}`}},
      {key: 'upliftGuideDtoC', label: `Uplift Guide – ${scoreMap.getLabel(-3)} > ${scoreMap.getLabel(-2)}`, input: TextareaInput, props: {label: `Uplift Guide – ${scoreMap.getLabel(-3)} > ${scoreMap.getLabel(-2)}`}},
      {key: 'upliftGuideCtoB', label: `Uplift Guide – ${scoreMap.getLabel(-2)} > ${scoreMap.getLabel(-1)}`, input: TextareaInput, props: {label: `Uplift Guide – ${scoreMap.getLabel(-2)} > ${scoreMap.getLabel(-1)}`}},
      {key: 'upliftGuideBtoA', label: `Uplift Guide – ${scoreMap.getLabel(-1)} > ${scoreMap.getLabel(0)}`, input: TextareaInput, props: {label: `Uplift Guide – ${scoreMap.getLabel(-1)} > ${scoreMap.getLabel(0)}`}}
    ]
  }
  
  getObjective(objective) {
    return objective.objective;
  }
  
  getQuestion(objective) {
    return objective.question;
  }
}